import { create } from 'zustand';

/**
 * Toast Notification Types
 */
export type ToastType = 'success' | 'error' | 'warning' | 'info';

/**
 * Optional inline action button (e.g. "Undo")
 */
export interface ToastAction {
  label: string;
  onClick?: () => void;
  /** Render the button disabled (e.g. feature not available yet) */
  disabled?: boolean;
  /** Native tooltip shown on the button (useful when disabled) */
  title?: string;
}

/**
 * Toast Interface
 */
export interface Toast {
  id: string;
  type: ToastType;
  message: string;
  duration?: number; // Auto-dismiss duration in milliseconds
  action?: ToastAction;
}

/**
 * Toast Store Interface
 */
interface ToastStore {
  toasts: Toast[];
  addToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (id: string) => void;
  clearAll: () => void;
}

/**
 * Maximum number of visible toasts
 */
const MAX_TOASTS = 5;

/**
 * Maximum message length
 */
const MAX_MESSAGE_LENGTH = 200;

/**
 * Sanitize toast message for security and UX
 * - Strip HTML tags to prevent XSS
 * - Limit length to prevent DOM overflow
 */
const sanitizeMessage = (message: string): string => {
  return message
    .replace(/<[^>]*>/g, '') // Strip HTML tags
    .slice(0, MAX_MESSAGE_LENGTH) // Limit length
    .trim();
};

/**
 * Toast Store
 *
 * Global state management for toast notifications
 * - Add toasts with auto-generated UUIDs
 * - Remove toasts by ID
 * - Clear all toasts
 * - Auto-limit to MAX_TOASTS visible
 * - Sanitize messages for security
 *
 * @example
 * const { addToast, removeToast } = useToastStore();
 * addToast({ type: 'success', message: 'Saved!', duration: 5000 });
 */
export const useToastStore = create<ToastStore>((set) => ({
  toasts: [],

  addToast: (toast) =>
    set((state) => {
      const newToast = {
        ...toast,
        message: sanitizeMessage(toast.message),
        id: crypto.randomUUID(),
      };

      // Keep only the most recent MAX_TOASTS
      const updatedToasts = [...state.toasts, newToast];
      if (updatedToasts.length > MAX_TOASTS) {
        return { toasts: updatedToasts.slice(-MAX_TOASTS) };
      }
      return { toasts: updatedToasts };
    }),

  removeToast: (id) =>
    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id),
    })),

  clearAll: () => set({ toasts: [] }),
}));
